// Imprest retirement configuration (Expenses → Setup).
// Design: docs/expenses/03-imprest-retirement-and-reimbursement-sla.md §4.
//
// HR owns this screen. It writes ONLY the imprest block — the spend limits and
// receipt thresholds that share the same rules document are Finance's and are
// not reachable from here, because the endpoint behind it merges rather than
// replaces.
//
// THE TWO EMAIL SWITCHES ARE THE POINT OF THIS FILE. One of them writes to every
// active employee. So: they default off, the screen states in plain words what
// is currently true, and it takes the answer from the SERVER rather than
// recomputing "enabled AND channels includes email" locally — a second
// implementation of that rule is how a page ends up reassuring somebody that
// sending is off while it is on.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Mail, BellRing, Info, AtSign, Languages } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Switch } from '@/features/shared/components/ui/switch';
import {
  getImprestPolicy, saveImprestPolicy,
  type ImprestPolicy, type ImprestPolicyEffective, type ImprestReminderConfig,
} from '@/lib/expensesApi';
import { useUnsavedGuard, usePendingGuard } from '@/features/shared/components/recovery/useFormGuards';

const input =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground ' +
  'focus:outline-none focus:ring-2 focus:ring-orange-500/40';

const hasChannel = (c: ImprestReminderConfig, name: string) =>
  (c.channels || []).some((x) => x.trim().toLowerCase() === name);

const withChannel = (c: ImprestReminderConfig, name: string, on: boolean): ImprestReminderConfig => {
  const rest = (c.channels || []).filter((x) => x.trim().toLowerCase() !== name);
  return { ...c, channels: on ? [...rest, name] : rest };
};

/** One sender: the master switch plus its channels. Rendered twice, because the
 *  two audiences are genuinely different and must be configurable apart — the
 *  universal reminder is a broadcast, the private notice is one person's money. */
const SenderControls: React.FC<{
  label: string;
  audience: string;
  value: ImprestReminderConfig;
  onChange: (v: ImprestReminderConfig) => void;
  /** What the SERVER says is true right now — not what the form is showing. */
  liveEmails: boolean;
  dirty: boolean;
}> = ({ label, audience, value, onChange, liveEmails, dirty }) => {
  const { t } = useTranslation('expenses');
  const email = hasChannel(value, 'email');
  const inApp = hasChannel(value, 'in_app');
  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-foreground">{label}</div>
          <div className="text-sm text-muted-foreground">{audience}</div>
        </div>
        <Switch checked={!!value.enabled} onCheckedChange={(on) => onChange({ ...value, enabled: on })} />
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="accent-orange-500"
            checked={email}
            disabled={!value.enabled}
            onChange={(e) => onChange(withChannel(value, 'email', e.target.checked))}
          />
          <Mail className="w-3.5 h-3.5" />
          {t('imprest.channel_email', { defaultValue: 'Email' })}
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="accent-orange-500"
            checked={inApp}
            disabled={!value.enabled}
            onChange={(e) => onChange(withChannel(value, 'in_app', e.target.checked))}
          />
          <BellRing className="w-3.5 h-3.5" />
          {t('imprest.channel_in_app', { defaultValue: 'In-app' })}
        </label>
      </div>

      {/* State of the world, from the server. Deliberately says "saved" when the
          form has moved on, so nobody reads an unsaved intention as a fact. */}
      <p className={`text-xs ${liveEmails ? 'text-orange-600 dark:text-orange-400' : 'text-muted-foreground'}`}>
        {dirty
          ? t('imprest.unsaved', { defaultValue: 'Not saved yet — nothing changes until you save.' })
          : liveEmails
            ? t('imprest.emails_on', { defaultValue: 'Currently emailing. Staff receive this by email.' })
            : t('imprest.emails_off', { defaultValue: 'Not emailing. Nothing is sent to anyone’s inbox.' })}
      </p>
    </div>
  );
};

export const ImprestPolicyPanel: React.FC = () => {
  const { t } = useTranslation('expenses');
  const [pol, setPol] = useState<ImprestPolicy | null>(null);
  const [live, setLive] = useState<ImprestPolicyEffective>({
    policyReminderEmails: false, privateNoticeEmails: false, fromName: '', replyTo: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Reload guard. These mirror the state this panel already keeps: dirty is
  // cleared only after a confirmed save, and saving is cleared in a finally,
  // so a failed save correctly keeps the hold.
  useUnsavedGuard(dirty, 'imprest-policy', 'imprest settings');
  usePendingGuard(saving, 'imprest-policy', 'imprest settings');

  const load = useCallback(() => {
    setLoading(true);
    getImprestPolicy()
      .then((r) => { setPol(r.policy); setLive(r.effective); setDirty(false); })
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load the imprest settings.')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const edit = (patch: Partial<ImprestPolicy>) => {
    setPol((p) => (p ? { ...p, ...patch } : p));
    setDirty(true);
  };

  const save = async () => {
    if (!pol) return;
    setSaving(true);
    try {
      const r = await saveImprestPolicy(pol);
      setPol(r.policy);
      setLive(r.effective);
      setDirty(false);
      // Name the consequence, not the operation. "Saved" tells an HR manager
      // nothing about whether their staff are now being written to.
      const mailing = [
        r.effective.policyReminderEmails && t('imprest.toast_reminder', { defaultValue: 'the monthly reminder' }),
        r.effective.privateNoticeEmails && t('imprest.toast_notices', { defaultValue: 'private notices' }),
      ].filter(Boolean).join(' and ');
      toast.success(mailing
        ? t('imprest.toast_saved_emailing', { defaultValue: 'Saved. Email is on for {{what}}.', what: mailing })
        : t('imprest.toast_saved_silent', { defaultValue: 'Saved. No email is sent to employees.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not save the imprest settings.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('imprest.loading', { defaultValue: 'Loading imprest settings…' })}
        </CardContent>
      </Card>
    );
  }
  if (!pol) return null;

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="text-base">
          {t('imprest.title', { defaultValue: 'Imprest retirement' })}
        </CardTitle>
        <CardDescription>
          {t('imprest.desc', {
            defaultValue:
              'When imprests must be retired, how long staff have afterwards, and who hears about it. Spend limits and receipt rules are set separately by Finance.',
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="block text-sm font-medium text-foreground mb-1">
              {t('imprest.cutoff_day', { defaultValue: 'Retire by day of month' })}
            </span>
            <input
              className={input} inputMode="numeric" value={String(pol.cutoff_day ?? '')}
              onChange={(e) => edit({ cutoff_day: Number(e.target.value) || 0 })}
            />
            <span className="block text-xs text-muted-foreground mt-1">
              {t('imprest.cutoff_hint', { defaultValue: 'A day past the end of a short month becomes that month’s last day.' })}
            </span>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-foreground mb-1">
              {t('imprest.grace', { defaultValue: 'Grace period (days)' })}
            </span>
            <input
              className={input} inputMode="numeric" value={String(pol.grace?.value ?? '')}
              onChange={(e) => edit({ grace: { ...pol.grace, value: Number(e.target.value) || 0 } })}
            />
            <span className="block text-xs text-muted-foreground mt-1">
              {t('imprest.grace_hint', { defaultValue: 'Counted from the cutoff, not from when Finance publishes.' })}
            </span>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-foreground mb-1">
              {t('imprest.sla', { defaultValue: 'Reimbursement SLA (days)' })}
            </span>
            <input
              className={input} inputMode="numeric" value={String(pol.reimbursement_sla?.value ?? '')}
              onChange={(e) => edit({ reimbursement_sla: { ...pol.reimbursement_sla, value: Number(e.target.value) || 0 } })}
            />
            <span className="block text-xs text-muted-foreground mt-1">
              {t('imprest.sla_hint', { defaultValue: 'Measured from final approval. A failed payment does not extend it.' })}
            </span>
          </label>
        </div>

        {/* The language the MESSAGE is written in — separate from the language
            this screen is in. It is a company setting because the platform holds
            no language against a person: the app's own switcher is browser-local
            and never saved, so there is nothing per-recipient to honour and
            offering a per-person choice would be a promise we cannot keep. */}
        <label className="block max-w-xs">
          <span className="block text-sm font-medium text-foreground mb-1 inline-flex items-center gap-1.5">
            <Languages className="w-3.5 h-3.5" />
            {t('imprest.language', { defaultValue: 'Language staff are written to in' })}
          </span>
          <select
            className={input}
            value={pol.message_language || 'en'}
            onChange={(e) => edit({ message_language: e.target.value })}
          >
            <option value="en">{t('imprest.lang_en', { defaultValue: 'English' })}</option>
            <option value="sw">{t('imprest.lang_sw', { defaultValue: 'Kiswahili' })}</option>
          </select>
          <span className="block text-xs text-muted-foreground mt-1">
            {t('imprest.language_hint', {
              defaultValue: 'Applies to the whole company. Everyone receives the reminder and their notice in this language.',
            })}
          </span>
        </label>

        <label className="block max-w-xs">
          <span className="block text-sm font-medium text-foreground mb-1">
            {t('imprest.timezone', { defaultValue: 'Timezone' })}
          </span>
          <input
            className={input} value={pol.timezone || ''} placeholder="Africa/Dar_es_Salaam"
            onChange={(e) => edit({ timezone: e.target.value })}
          />
        </label>

        <div className="grid md:grid-cols-2 gap-3">
          <SenderControls
            label={t('imprest.sender_reminder', { defaultValue: 'Monthly policy reminder' })}
            audience={t('imprest.sender_reminder_audience', {
              defaultValue: 'Every active employee, whether or not they owe anything. States the policy only — no balances, no amounts.',
            })}
            value={pol.policy_reminder}
            onChange={(v) => edit({ policy_reminder: v })}
            liveEmails={live.policyReminderEmails}
            dirty={dirty}
          />
          <SenderControls
            label={t('imprest.sender_notices', { defaultValue: 'Private notices' })}
            audience={t('imprest.sender_notices_audience', {
              defaultValue: 'Only employees with something outstanding, and only their own items. Sent when Finance publishes a review.',
            })}
            value={pol.private_notices}
            onChange={(v) => edit({ private_notices: v })}
            liveEmails={live.privateNoticeEmails}
            dirty={dirty}
          />
        </div>

        {/* WHO THE MAIL APPEARS TO BE FROM. The address is always the platform's
            own verified domain — a tenant address would fail their DMARC and let
            anyone put anyone's name on an envelope — so the tenant sets a NAME
            and a reply-to, and the preview below is rendered by the server from
            the same code that writes the real header. */}
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div>
            <div className="font-semibold text-foreground">
              {t('imprest.sender_title', { defaultValue: 'How the email appears' })}
            </div>
            <div className="text-sm text-muted-foreground">
              {t('imprest.sender_desc', {
                defaultValue:
                  'Staff see your company name, and replies reach a real person. The message is still sent from KaziPeople’s mail servers, so your own domain needs no setup.',
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm font-medium text-foreground mb-1">
                {t('imprest.sender_name', { defaultValue: 'Company name shown to staff' })}
              </span>
              <input
                className={input} placeholder="Fern Group"
                value={pol.sender?.display_name || ''}
                onChange={(e) => edit({ sender: { ...(pol.sender || { display_name: '', reply_to: '' }), display_name: e.target.value } })}
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-foreground mb-1">
                {t('imprest.sender_reply_to', { defaultValue: 'Replies go to' })}
              </span>
              <input
                className={input} inputMode="email" placeholder="finance@yourcompany.com"
                value={pol.sender?.reply_to || ''}
                onChange={(e) => edit({ sender: { ...(pol.sender || { display_name: '', reply_to: '' }), reply_to: e.target.value } })}
              />
              <span className="block text-xs text-muted-foreground mt-1">
                {t('imprest.sender_reply_hint', {
                  defaultValue: 'Leave blank and replies go nowhere — someone will reply asking about their own imprest.',
                })}
              </span>
            </label>
          </div>

          {/* The live header, from the server. Hidden while dirty for the same
              reason the emailing claims are: it would be showing an intention. */}
          {!dirty && (
            <div className="rounded-md bg-muted/50 px-3 py-2 text-xs font-mono text-muted-foreground space-y-0.5">
              <div className="flex items-start gap-2">
                <AtSign className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div className="min-w-0 break-words">
                  <div>From: {live.fromName || 'KaziPeople'} &lt;noreply@kazipeople.com&gt;</div>
                  <div>
                    Reply-To: {live.replyTo || t('imprest.sender_none', { defaultValue: '(none — replies go nowhere)' })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {t('imprest.schedule_note', {
            defaultValue:
              'The monthly reminder also needs its automation switched on under Settings → Automation. Both must be on before anything is sent.',
          })}
        </p>

        <div className="flex items-center gap-2">
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" disabled={saving || !dirty} onClick={save}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {t('imprest.save', { defaultValue: 'Save imprest settings' })}
          </Button>
          {dirty && (
            <Button size="sm" variant="ghost" disabled={saving} onClick={load}>
              {t('imprest.discard', { defaultValue: 'Discard changes' })}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ImprestPolicyPanel;
